import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../Context/CartContext';
import { useLanguage, getLanguageText } from '../Context/LanguageContext';
import { getActiveDiscounts } from '../firebase/services';
import { getBestDiscountForProduct, computeDiscountedPrice, formatDiscountBadge } from '../Utils/discounts';
import { useWishlist } from '../Context/WishlistContext';

export default function ProductCard({ product, categories = [] }) {
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { language } = useLanguage();
  const [matchedDiscount, setMatchedDiscount] = React.useState(null);

  const productStock = React.useMemo(() => {
    // If product has taste variants, treat it as in-stock when ANY taste has stock.
    if (product?.tastes && Array.isArray(product.tastes) && product.tastes.length > 0) {
      const stocks = product.tastes
        .map(t => Number(t?.stock ?? 0))
        .filter(n => Number.isFinite(n));
      if (stocks.length > 0) return Math.max(...stocks);
    }
    return Number(product?.stock ?? 0) || 0;
  }, [product?.stock, product?.tastes]);

  const isOutOfStock = productStock <= 0;

  const basePrice = React.useMemo(() => {
    if (product?.tastes && Array.isArray(product.tastes) && product.tastes.length > 0) {
      const first = product.tastes[0];
      const p = Number(first?.price ?? first ?? 0);
      return Number.isFinite(p) ? p : 0;
    }
    if (product?.tastePrices && Array.isArray(product.tastePrices) && product.tastePrices.length > 0) {
      const p = Number(product.tastePrices[0] ?? 0);
      return Number.isFinite(p) ? p : 0;
    }
    const p = Number(product?.price ?? 0);
    return Number.isFinite(p) ? p : 0;
  }, [product?.price, product?.tastes, product?.tastePrices]);

  const pricing = React.useMemo(() => {
    return computeDiscountedPrice(basePrice, matchedDiscount);
  }, [basePrice, matchedDiscount]);

  const discountBadgeText = React.useMemo(() => {
    return formatDiscountBadge(matchedDiscount);
  }, [matchedDiscount]);

  const hasActiveDiscount = !!matchedDiscount && pricing.finalPrice < pricing.basePrice;
  
  // Check if product has any active discount
  React.useEffect(() => {
    const checkDiscount = async () => {
      try {
        const activeDiscounts = await getActiveDiscounts();
        const best = getBestDiscountForProduct(product, activeDiscounts);
        setMatchedDiscount(best || null);
      } catch (error) {
        console.error('Error checking discount:', error);
        setMatchedDiscount(null);
      }
    };
    
    checkDiscount();
  }, [product.id, product.category, product.categoryId, (product.categories || []).join('|')]);
  
  // Determine the full product name for display
  let fullProductName = product.name; // Prefer the combined string "En / Ar"
  
  // If combined string is missing but we have separate fields, construct it or use object
  if (!fullProductName) {
      if (product.nameEn && product.nameAr) {
          fullProductName = { en: product.nameEn, ar: product.nameAr };
      } else {
          fullProductName = product.nameEn || product.nameAr || 'Unnamed Product';
      }
  } else if (typeof fullProductName === 'object' && !fullProductName.en && !fullProductName.ar) {
      // Handle legacy object format if any
      fullProductName = 'Unnamed Product';
  }

  const productName = getLanguageText(fullProductName, language);
  
  // Handle images: prefer imageUrls array, then images array, then single imageUrl
  const productImage = (product.imageUrls && product.imageUrls.length > 0 && product.imageUrls[0]) || 
                       (product.images && product.images[0]) || 
                       product.imageUrl || 
                       'https://placehold.co/360x360?text=No+Image';

  // Extract first part of name (before "/" if bilingual) for slug
  const firstNamePart = typeof fullProductName === 'string' ? fullProductName.split(' / ')[0] : (fullProductName.en || fullProductName);
  const productSlug = typeof firstNamePart === 'string' ? firstNamePart.toLowerCase().replace(/\s+/g, '-') : 'product';
  
  const toSlug = (value, fallback) => {
    const raw = (value ?? '').toString();
    const firstPart = raw.split(' / ')[0].trim().toLowerCase();
    const cleaned = firstPart
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\u0600-\u06FF-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    return cleaned || fallback || 'uncategorized';
  };

  // Extract category slug.
  // Products may store categories as Firestore IDs; resolve to a name when possible.
  const rawPrimary = (product?.categories && Array.isArray(product.categories) && product.categories.length > 0)
    ? product.categories[0]
    : (product?.categoryId || product?.category);

  const resolvedCategory = categories.find(c => c?.id === rawPrimary) || null;
  const categoryLabel = resolvedCategory
    ? (resolvedCategory.nameEn || resolvedCategory.name || resolvedCategory.nameAr)
    : (typeof rawPrimary === 'string' ? rawPrimary : 'uncategorized');

  const categorySlug = toSlug(categoryLabel, 'uncategorized');
  
  const productLink = `/products/${categorySlug}/${productSlug}`;
  const wished = isInWishlist ? isInWishlist(product) : false;

  return (
    <div className="product-card-new">
      <div className="card-image-wrapper">
        {hasActiveDiscount && (
          <span className="discount-badge">{discountBadgeText || 'OFF'}</span>
        )}

        {isOutOfStock && (
          <span className={`outofstock-badge ${hasActiveDiscount ? 'stacked' : ''}`}>Out of Stock</span>
        )}
        <Link to={productLink}>
            <img 
                src={productImage} 
                alt={productName} 
                className="product-img"
                onError={(e) => {e.target.src = 'https://placehold.co/360x360?text=Error'}}
            />
        </Link>
      </div>
      
      <div className="card-details">
        <h3 className="product-title">
            <Link to={productLink}>{productName}</Link>
        </h3>
        
        <div className="price-wrapper">
            {matchedDiscount && pricing.finalPrice < pricing.basePrice ? (
              <>
                <span className="old-price">{pricing.basePrice} EGP</span>
                <span className="current-price">{pricing.finalPrice} EGP</span>
              </>
            ) : (
              <span className="current-price">
                {basePrice > 0 ? `${basePrice} EGP` : 'Price on request'}
              </span>
            )}
        </div>

        <div className="card-actions">
            <button
              className="wishlist-btn"
              aria-label="Wishlist"
              onClick={() => {
                if (!toggleWishlist) return;
                toggleWishlist({ ...product, baseProductId: product.id });
              }}
            >
                <ion-icon name={wished ? 'heart' : 'heart-outline'}></ion-icon>
            </button>
            <button
              className="add-cart-btn"
              disabled={isOutOfStock}
              onClick={() => {
              if (isOutOfStock) return;
              // Get first taste and its price
              let selectedTaste = null;
              let selectedPrice = 0;
              
              if (product.tastes && Array.isArray(product.tastes) && product.tastes.length > 0) {
                selectedTaste = product.tastes[0];
                selectedPrice = product.tastes[0].price || product.tastes[0] || 0;
              } else if (product.tastePrices && Array.isArray(product.tastePrices) && product.tastePrices.length > 0) {
                selectedPrice = product.tastePrices[0];
              } else if (product.price) {
                selectedPrice = product.price;
              }
              
              const productToAdd = {
                ...product,
                baseProductId: product.id,
                basePrice: selectedPrice,
                selectedPrice: selectedPrice,
                price: selectedPrice,
                selectedTaste: selectedTaste,
                // Create unique ID for product+taste combination
                id: selectedTaste ? `${product.id}_${selectedTaste.name || 'taste0'}` : product.id
              };
              
              addToCart(productToAdd);
            }}>
                <ion-icon name="cart-outline"></ion-icon>
                <span>{isOutOfStock ? 'Out of Stock' : 'Add to Cart'}</span>
            </button>
        </div>
      </div>

      <style>{`
        .product-card-new {
            border: 1px solid var(--portland-orange);
            border-radius: 8px;
            overflow: hidden;
            background: #fff;
            display: flex;
            flex-direction: column;
            height: 100%;
            transition: box-shadow 0.3s ease;
            position: relative;
        }
        .product-card-new:hover {
            box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        .card-image-wrapper {
            position: relative;
            padding-top: 100%; /* 1:1 Aspect Ratio */
            overflow: hidden;
            border-bottom: 1px solid #eee;
        }

        .outofstock-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #fde8e8;
          color: #9b1c1c;
          padding: 4px 10px;
          font-size: 0.8rem;
          font-weight: 700;
          border-radius: 9999px;
          z-index: 2;
          border: 1px solid #fca5a5;
        }
        .card-badge {
            position: absolute;
            top: 10px;
            left: 10px;  /* Move to left side */
            background: #ff4d4f; 
            color: #fff;
            padding: 4px 10px;
            font-size: 0.8rem;
            font-weight: bold;
            border-radius: 4px;
            z-index: 2;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .discount-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          left: auto;
          background: #ff4d4f;
          color: #fff;
          padding: 4px 10px;
          font-size: 0.8rem;
          font-weight: 800;
          border-radius: 9999px;
          z-index: 2;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: auto;
          max-width: calc(100% - 20px);
          white-space: nowrap;
        }

        .outofstock-badge.stacked {
          top: 44px;
        }
        .product-img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: contain;
            padding: 15px;
            transition: transform 0.3s ease;
        }
        .product-card-new:hover .product-img {
            transform: scale(1.05);
        }
        .card-details {
            padding: 12px;
            text-align: center;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
        }
        .product-title {
            font-size: 1.5rem;
            margin-bottom: 8px;
            font-weight: 700;
            color: #1B92A8;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            min-height: 2.4em;
            line-height: 1.3;
        }
        .product-title a {
            color: #1B92A8;
            text-decoration: none;
        }
        .product-title a:hover {
            color: #155a75;
        }
        .price-wrapper {
            margin-bottom: 12px;
            font-weight: 700;
            font-size: 1.6rem;
            color: #1B92A8;
            display: flex;
            justify-content: center;
            gap: 10px;
            align-items: baseline;
            flex-wrap: wrap;
        }
        .old-price {
          font-size: 1.1rem;
          color: #6b7280;
          text-decoration: line-through;
          font-weight: 700;
        }
        .card-actions {
            margin-top: auto;
            display: flex;
            gap: 8px;
        }
        .wishlist-btn {
            width: 38px;
            height: 38px;
            border: 2px solid var(--portland-orange);
            border-radius: 6px;
            background: #fff;
            color: var(--portland-orange);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.4rem;
            cursor: pointer;
            transition: all 0.2s;
        }
        .wishlist-btn:hover {
            background: #f0f0f0;
        }
        .add-cart-btn {
            flex: 1;
            height: 42px;
          background: var(--portland-orange);
            color: #fff;
            border: none;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            font-weight: 700;
            font-size: 1.2rem;
            cursor: pointer;
            transition: background 0.2s;
        }
        .add-cart-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .add-cart-btn:hover {
            background: #0a7c76;
        }
        .add-cart-btn ion-icon {
            font-size: 1.3rem;
        }
        @media (max-width: 575px) {
            .product-title {
            font-size: 1.2rem;
            line-height: 1.25;
            min-height: 2.8em;
            }
            .price-wrapper {
            font-size: 1.3rem;
            margin-bottom: 10px;
            }
            .wishlist-btn {
                width: 32px;
                height: 32px;
                font-size: 1.2rem;
            }
            .add-cart-btn {
            height: 38px;
            font-size: 1rem;
            }
            .card-details {
            padding: 12px;
            }
        }
      `}</style>
    </div>
  );
}
